import {
	ApiAccountBlockedError,
	ApiAuthenticationError,
	ApiNetworkError,
	ApiServiceError,
	ApiVersionMismatchError,
	type ErrorResponse
} from '../types/api.js';

type AuthErrorInterceptor = () => void;
type VersionMismatchInterceptor = () => void;
type AccountBlockedInterceptor = () => void;

let appVersionProvider: () => string = () => '';

/** Register a provider for the App-Version request header */
export function setAppVersionProvider(provider: () => string): void {
	appVersionProvider = provider;
}

function appVersionHeader(): string {
	return appVersionProvider();
}

let accessToken: string | null = null;
let authErrorInterceptor: AuthErrorInterceptor | null = null;
let versionMismatchInterceptor: VersionMismatchInterceptor | null = null;
let accountBlockedInterceptor: AccountBlockedInterceptor | null = null;

/** Set the access token used for all subsequent API requests */
export function setAccessToken(token: string | null): void {
	accessToken = token;
}

/** Register a callback invoked on HTTP 401 (typically triggers logout) */
export function setAuthErrorInterceptor(interceptor: AuthErrorInterceptor): void {
	authErrorInterceptor = interceptor;
}

/** Register a callback invoked on HTTP 426 (version mismatch — triggers required update) */
export function setVersionMismatchInterceptor(interceptor: VersionMismatchInterceptor): void {
	versionMismatchInterceptor = interceptor;
}

/** Register a callback invoked on HTTP 423 (the account is blocked — triggers a terminal overlay) */
export function setAccountBlockedInterceptor(interceptor: AccountBlockedInterceptor): void {
	accountBlockedInterceptor = interceptor;
}

async function handleErrorResponse(response: Response): Promise<never> {
	if (response.status === 426) {
		versionMismatchInterceptor?.();
		throw new ApiVersionMismatchError();
	}

	// Must precede the body parse below: the 423 carries a structured body, which would otherwise become an
	// ApiServiceError and never reach the interceptor.
	if (response.status === 423) {
		accountBlockedInterceptor?.();
		throw new ApiAccountBlockedError();
	}

	let errorResponse: ErrorResponse | null = null;
	try {
		const text = await response.text();
		const parsed = JSON.parse(text);
		// Only treat as ErrorResponse if it has the expected shape
		if (parsed && Array.isArray(parsed.errorMessages)) {
			errorResponse = parsed as ErrorResponse;
		}
	} catch {
		// Could not parse error response body
	}

	if (errorResponse) {
		throw new ApiServiceError(errorResponse);
	}

	if (response.status === 401) {
		authErrorInterceptor?.();
		throw new ApiAuthenticationError();
	}

	throw new Error(`Request failed with status ${response.status}`);
}

function buildHeaders(extra?: HeadersInit): Headers {
	const headers = new Headers(extra);
	if (!headers.has('Content-Type')) {
		headers.set('Content-Type', 'application/json');
	}
	if (accessToken) {
		headers.set('Authorization', `bearer ${accessToken}`);
	}
	headers.set('App-Version', appVersionHeader());
	return headers;
}

/** Typed GET request */
export async function get<T>(url: string): Promise<T> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, { headers: buildHeaders() });
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}

	return (await response.json()) as T;
}

/** Typed POST request with JSON body, returning typed response */
export async function post<TBody, TResponse>(url: string, body: TBody): Promise<TResponse> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'POST',
			headers: buildHeaders(),
			body: JSON.stringify(body)
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}

	return (await response.json()) as TResponse;
}

/** POST request with JSON body, no response body expected */
export async function postVoid<TBody>(url: string, body?: TBody): Promise<void> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'POST',
			headers: buildHeaders(),
			body: JSON.stringify(body ?? {})
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}
}

/** Typed PUT request with JSON body */
export async function put<TBody>(url: string, body: TBody): Promise<void> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'PUT',
			headers: buildHeaders(),
			body: JSON.stringify(body)
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}
}

/** PATCH request (no body) */
export async function patch(url: string): Promise<void> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'PATCH',
			headers: buildHeaders()
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}
}

/** DELETE request */
export async function del(url: string): Promise<void> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'DELETE',
			headers: buildHeaders()
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}
}

/** POST request with FormData (multipart/form-data for file uploads) */
export async function postFormData<TResponse>(url: string, formData: FormData): Promise<TResponse> {
	const headers = new Headers();
	if (accessToken) {
		headers.set('Authorization', `bearer ${accessToken}`);
	}
	headers.set('App-Version', appVersionHeader());
	// Do NOT set Content-Type — browser sets it with correct boundary for multipart

	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'POST',
			headers,
			body: formData
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}

	return (await response.json()) as TResponse;
}

/** POST FormData with upload progress tracking (uses XMLHttpRequest) */
export function postFormDataWithProgress<TResponse>(
	url: string,
	formData: FormData,
	onProgress: (progress: number) => void
): Promise<TResponse> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', `/api/v1/${url}`);

		if (accessToken) {
			xhr.setRequestHeader('Authorization', `bearer ${accessToken}`);
		}
		xhr.setRequestHeader('App-Version', appVersionHeader());

		xhr.upload.addEventListener('progress', (e) => {
			if (e.lengthComputable) {
				onProgress(e.loaded / e.total);
			}
		});

		xhr.addEventListener('load', () => {
			if (xhr.status === 426) {
				versionMismatchInterceptor?.();
				reject(new ApiVersionMismatchError());
				return;
			}

			if (xhr.status === 423) {
				accountBlockedInterceptor?.();
				reject(new ApiAccountBlockedError());
				return;
			}

			if (xhr.status === 401) {
				authErrorInterceptor?.();
				reject(new ApiAuthenticationError());
				return;
			}

			if (xhr.status >= 200 && xhr.status < 300) {
				resolve(JSON.parse(xhr.responseText) as TResponse);
				return;
			}

			try {
				const parsed = JSON.parse(xhr.responseText);
				if (parsed && Array.isArray(parsed.errorMessages)) {
					reject(new ApiServiceError(parsed as ErrorResponse));
					return;
				}
			} catch {
				/* not a structured error */
			}

			reject(new Error(`Request failed with status ${xhr.status}`));
		});

		xhr.addEventListener('error', () => {
			reject(new ApiNetworkError(new Error('Network error')));
		});

		xhr.send(formData);
	});
}

/** POST FormData with no response body expected */
export async function postFormDataVoid(url: string, formData: FormData): Promise<void> {
	const headers = new Headers();
	if (accessToken) {
		headers.set('Authorization', `bearer ${accessToken}`);
	}
	headers.set('App-Version', appVersionHeader());

	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'POST',
			headers,
			body: formData
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}
}

/** GET request returning raw bytes (for PDF downloads, etc.) */
export async function getBytes(url: string): Promise<Blob> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			headers: buildHeaders()
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}

	return await response.blob();
}

/** POST request with JSON body, returning raw bytes (for generated documents, labels, etc.) */
export async function postBytes<TBody>(url: string, body: TBody): Promise<Blob> {
	let response: Response;
	try {
		response = await fetch(`/api/v1/${url}`, {
			method: 'POST',
			headers: buildHeaders(),
			body: JSON.stringify(body)
		});
	} catch (e) {
		throw new ApiNetworkError(e);
	}

	if (!response.ok) {
		await handleErrorResponse(response);
	}

	return await response.blob();
}
